import { Injectable, inject, computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from './auth.service';
import { BusinessContextService } from './business-context.service';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { ModuleService, SIDEBAR_GROUPS } from '../../shared/services/module.service';
import { NavItem, User } from '../../shared/models';

/** Grupo de navegación para el sidebar */
export interface NavGroup {
    key: string;
    label: string;
    icon: string;
    items: NavItem[];
}

/**
 * Items fijos: siempre visibles para cualquier usuario autenticado.
 */
const FIXED_NAV_ITEMS: NavItem[] = [
    {
        label: 'Dashboard',
        icon: 'solar:home-bold-duotone',
        route: '/app/dashboard',
        exact: true,
    },
];

/**
 * Items exclusivos de SuperAdmin (no vienen de system_modules).
 */
const SA_NAV_ITEMS: NavItem[] = [];

/**
 * Items para administradores de negocio (no SuperAdmin).
 */
const BIZ_ADMIN_NAV_ITEMS: NavItem[] = [];

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private authService = inject(AuthService);
    private bizCtx = inject(BusinessContextService);
    private moduleService = inject(ModuleService);
    private router = inject(Router);
    private location = inject(Location);

    private _user: Signal<User | null> = toSignal(this.authService.currentUser$, {
        initialValue: this.authService.currentUserValue,
    });

    /**
     * Menú plano reactivo.
     */
    readonly filteredMenu = computed<NavItem[]>(() => {
        return this.groupedMenu().flatMap(g => g.items);
    });

    /**
     * Menú agrupado reactivo — secciones con separadores.
     * Implementa lógica dinámica basada en rol de SuperAdmin y Contexto de Negocio.
     */
    readonly groupedMenu = computed<NavGroup[]>(() => {
        const user = this._user();
        const isSuperAdmin = user?.isSuperAdmin ?? this.authService.isSuperAdmin;
        const ctxPerms = this.bizCtx.permissions();
        const allowedByBiz = this.bizCtx.allowedActions();
        const hasBizContext = this.bizCtx.hasContext();

        if (!hasBizContext && !isSuperAdmin) {
            return [];
        }

        const modules = this.moduleService.allModules();
        const groups: NavGroup[] = [];

        // 1. Grupo "Principal" (Dashboard)
        groups.push({
            key: 'fixed',
            label: '',
            icon: '',
            items: [...FIXED_NAV_ITEMS],
        });

        // 2. Grupos Dinámicos: clinic, finance, admin, system
        for (const g of SIDEBAR_GROUPS) {
            const groupMenuItems = modules
                .filter(mod => {
                    if (mod.group !== g.key) return false;

                    // Lógica para grupo SISTEMA
                    if (g.key === 'system') {
                        // SuperAdmin ve todo el grupo sistema siempre
                        if (isSuperAdmin) return true;
                        // Otros ven solo si tienen permisos directos
                        return mod.permissions.some(p => ctxPerms.includes(p));
                    }

                    // Lógica para grupos de NEGOCIO (clinic, finance, admin)
                    // Solo visibles si hay un negocio seleccionado
                    if (!hasBizContext) return false;

                    // Si hay negocio seleccionado:
                    // SuperAdmin ve todo lo que el negocio tiene permitido
                    if (isSuperAdmin) {
                        return allowedByBiz.includes('*') || allowedByBiz.some(p => p.startsWith(mod.key + ':'));
                    }

                    // Colaborador/Admin normal ve solo lo que TIENE permitido (memebresía)
                    // (la membresía ya está filtrada por allowedActions del negocio en el backend)
                    return mod.permissions.some(p => ctxPerms.includes(p));
                })
                .sort((a, b) => a.order - b.order)
                .map(m => ({
                    label: m.label,
                    icon: m.icon,
                    route: m.route,
                }));

            if (groupMenuItems.length > 0) {
                groups.push({
                    key: g.key,
                    label: g.label,
                    icon: g.icon,
                    items: groupMenuItems,
                });
            }
        }

        // 3. Inyección de Items Estáticos (Mi Negocio, etc.)
        const saItems = [...SA_NAV_ITEMS];
        const bizItems = [...BIZ_ADMIN_NAV_ITEMS];

        if (isSuperAdmin) {
            this.injectItems(groups, 'system', 'Sistema', 'solar:server-bold-duotone', saItems);
            if (hasBizContext) {
                this.injectItems(groups, 'clinic', 'Clínica', 'solar:health-bold-duotone', bizItems);
            }
        } else if (hasBizContext) {
            this.injectItems(groups, 'clinic', 'Clínica', 'solar:health-bold-duotone', bizItems);
        }

        return groups;
    });

    /**
     * Helper para inyectar items manuales en grupos existentes o crear nuevos
     */
    private injectItems(groups: NavGroup[], groupKey: string, label: string, icon: string, items: NavItem[]) {
        if (items.length === 0) return;

        const filteredItems = items.filter(item => !item.permission || this.authService.hasPermission(item.permission));
        if (filteredItems.length === 0) return;

        let group = groups.find(g => g.key === groupKey);
        if (!group) {
            group = { key: groupKey, label, icon, items: [] };
            groups.push(group);
        }

        // Evitar duplicados por ruta
        for (const item of filteredItems) {
            if (!group.items.some(i => i.route === item.route)) {
                group.items.unshift(item);
            }
        }
    }

    /**
     * Navegación hacia atrás global.
     */
    goBack() {
        if (this.router.url.includes('/auth/login')) {
            this.router.navigateByUrl('/');
        } else {
            this.location.back();
        }
    }
}
